import { ELayoutPosition, ESdChangeReason, IDisplayItem, IScreenDesign, SpecialDIIds } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import _ from "lodash";
import { connect } from "react-redux";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaDropdownMenu, {
  KuikaDropdownMenuItemType,
  KuikaDropdownMenuPlacement
} from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import "./master-layout-component.scss";
import {
  describeLayoutRegion,
  IMasterLayout,
  MASTER_LAYOUT_PART_NAMES,
  MasterLayoutPart,
  masterLayoutKey,
  parseMasterLayoutKey
} from "./master-layout-regions";

export interface IMasterLayoutComponentState {}

export interface IMasterLayoutComponentProps {}

// Each menu opens away from the edge its control is docked on, so it never covers the window.
const MENU_PLACEMENT: Record<MasterLayoutPart, KuikaDropdownMenuPlacement> = {
  h: "bottomCenter",
  f: "topCenter",
  l: "bottomLeft",
  r: "bottomRight"
};

const MASTER_LAYOUT_PARTS: readonly MasterLayoutPart[] = ["h", "l", "r", "f"];

class MasterLayoutComponent extends PureSdComponent<
  IMasterLayoutComponentProps & IWithSDProps & IWithContextProp,
  IMasterLayoutComponentState
> {
  constructor(props: IMasterLayoutComponentProps & IWithSDProps & IWithContextProp) {
    super(props);
  }

  handleLayoutSelect = (
    hPosition: ELayoutPosition,
    fPosition: ELayoutPosition,
    lPosition: ELayoutPosition,
    rPosition: ELayoutPosition,
    activePart?: "h" | "f" | "l" | "r"
  ) => {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    const selectedDI: IDisplayItem | undefined = this.props.sd.getSelectedDisplayItem(sd);
    if (activePart === "h" && hPosition !== 0) {
      this.props.sd.setSelectedDisplayItem(SpecialDIIds.layoutHeaderDI.toString());
    } else if (activePart === "f" && fPosition !== 0) {
      this.props.sd.setSelectedDisplayItem(SpecialDIIds.layoutFooterDI.toString());
    } else if (activePart === "l" && lPosition !== 0) {
      this.props.sd.setSelectedDisplayItem(SpecialDIIds.layoutLeftDI.toString());
    } else if (activePart === "r" && rPosition !== 0) {
      this.props.sd.setSelectedDisplayItem(SpecialDIIds.layoutRightDI.toString());
    }
    if (
      (activePart === "h" && hPosition === 0) ||
      (activePart === "f" && fPosition === 0) ||
      (activePart === "l" && lPosition === 0) ||
      (activePart === "r" && rPosition === 0)
    ) {
      this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
    }
    this.props.sd.setLayoutPosition(hPosition, fPosition, lPosition, rPosition);
  };

  public get currentH(): ELayoutPosition {
    const layoutPosition = this.props.sd.getLayoutPosition(SpecialDIIds.layoutHeaderDI);
    return layoutPosition;
  }

  public get currentF(): ELayoutPosition {
    const layoutPosition = this.props.sd.getLayoutPosition(SpecialDIIds.layoutFooterDI);
    return layoutPosition;
  }

  public get currentL(): ELayoutPosition {
    const layoutPosition = this.props.sd.getLayoutPosition(SpecialDIIds.layoutLeftDI);
    return layoutPosition;
  }

  public get currentR(): ELayoutPosition {
    const layoutPosition = this.props.sd.getLayoutPosition(SpecialDIIds.layoutRightDI);
    return layoutPosition;
  }

  getHorizontalCaseList = (): [ELayoutPosition, ELayoutPosition, ELayoutPosition][] => {
    if (this.currentL === 0 && this.currentR === 0) {
      return [
        [0, 0, 0],
        [0, 1, 0]
      ];
    }
    if (this.currentL === 0) {
      return [
        [0, 0, this.currentR],
        [0, 1, this.currentR],
        [0, 2, 1]
      ];
    }
    if (this.currentR === 0) {
      return [
        [this.currentL, 0, 0],
        [this.currentL, 1, 0],
        [1, 2, 0]
      ];
    }
    return _.uniq([
      [this.currentL, 0, this.currentR],
      [this.currentL, 1, this.currentR],
      [2, 2, 1],
      [1, 2, 2],
      [1, 2, 1]
    ]);
  };

  getVerticalCaseList = (): [ELayoutPosition, ELayoutPosition, ELayoutPosition][] => {
    if (this.currentH === ELayoutPosition.invisible && this.currentF === ELayoutPosition.invisible) {
      return [
        [ELayoutPosition.invisible, ELayoutPosition.invisible, ELayoutPosition.invisible],
        [ELayoutPosition.invisible, ELayoutPosition.primary, ELayoutPosition.invisible]
      ];
    }

    if (this.currentH === ELayoutPosition.invisible) {
      return [
        [ELayoutPosition.invisible, ELayoutPosition.invisible, this.currentF],
        [ELayoutPosition.invisible, ELayoutPosition.primary, this.currentF]
      ];
    }

    if (this.currentF === ELayoutPosition.invisible) {
      return [
        [this.currentH, ELayoutPosition.invisible, ELayoutPosition.invisible],
        [this.currentH, ELayoutPosition.primary, ELayoutPosition.invisible]
      ];
    }

    return _.uniq([
      [this.currentH, ELayoutPosition.invisible, this.currentF],
      ...this.getHF(),
      [ELayoutPosition.secondary, ELayoutPosition.primary, ELayoutPosition.secondary],
      [ELayoutPosition.primary, ELayoutPosition.primary, ELayoutPosition.secondary],
      [ELayoutPosition.secondary, ELayoutPosition.primary, ELayoutPosition.primary]
    ]) as [ELayoutPosition, ELayoutPosition, ELayoutPosition][];
  };

  getHF = (): [ELayoutPosition, ELayoutPosition, ELayoutPosition][] => {
    if (this.currentH !== this.currentF) {
      return [
        [this.currentH, ELayoutPosition.primary, this.currentF],
        [this.currentH, ELayoutPosition.secondary, this.currentF]
      ];
    }

    return [[this.currentH, ELayoutPosition.primary, this.currentF]];
  };

  setFAuto = (l: ELayoutPosition, r: ELayoutPosition): ELayoutPosition => {
    let f = this.currentF;
    if (f === 2 && ((this.currentR === 2 && r === 1) || (this.currentL === 2 && l === 1))) {
      f = 1;
    }
    return f;
  };

  setHAuto = (l: ELayoutPosition, r: ELayoutPosition): ELayoutPosition => {
    let h = this.currentH;
    if (h === 2 && ((this.currentR === 2 && r === 1) || (this.currentL === 2 && l === 1))) {
      h = 1;
    }
    return h;
  };

  setLAuto = (h: ELayoutPosition, f: ELayoutPosition): ELayoutPosition => {
    let l = this.currentL;
    if (l === 2 && ((this.currentH === 2 && h === 1) || (this.currentF === 2 && f === 1))) {
      l = 1;
    }
    return l;
  };

  setRAuto = (h: ELayoutPosition, f: ELayoutPosition): ELayoutPosition => {
    let r = this.currentR;
    if (r === 2 && ((this.currentH === 2 && h === 1) || (this.currentF === 2 && f === 1))) {
      r = 1;
    }
    return r;
  };

  get currentLayout(): IMasterLayout {
    return { h: this.currentH, f: this.currentF, l: this.currentL, r: this.currentR };
  }

  /**
   * The layouts one region's menu offers, before `getRegionOptions` settles them. Each case list
   * holds the legal combinations for the other regions; the `set*Auto` helpers adjust the region
   * the list does not mention.
   */
  getCandidateLayouts = (part: MasterLayoutPart): IMasterLayout[] => {
    if (part === "h") {
      return this.getHorizontalCaseList().map(
        (lhr: [ELayoutPosition, ELayoutPosition, ELayoutPosition]): IMasterLayout => ({
          h: lhr[1],
          f: this.setFAuto(lhr[0], lhr[2]),
          l: lhr[0],
          r: lhr[2]
        })
      );
    }
    if (part === "f") {
      return this.getHorizontalCaseList().map(
        (lfr: [ELayoutPosition, ELayoutPosition, ELayoutPosition]): IMasterLayout => ({
          h: this.setHAuto(lfr[0], lfr[2]),
          f: lfr[1],
          l: lfr[0],
          r: lfr[2]
        })
      );
    }
    if (part === "l") {
      return this.getVerticalCaseList().map(
        (hlf: [ELayoutPosition, ELayoutPosition, ELayoutPosition]): IMasterLayout => ({
          h: hlf[0],
          f: hlf[2],
          l: hlf[1],
          r: this.setRAuto(hlf[0], hlf[2])
        })
      );
    }
    return this.getVerticalCaseList().map(
      (hrf: [ELayoutPosition, ELayoutPosition, ELayoutPosition]): IMasterLayout => ({
        h: hrf[0],
        f: hrf[2],
        l: this.setLAuto(hrf[0], hrf[2]),
        r: hrf[1]
      })
    );
  };

  /** One region's options: a region that is off stays off unless it is the one being changed; duplicates collapse. */
  getRegionOptions = (part: MasterLayoutPart): IMasterLayout[] => {
    const current: IMasterLayout = this.currentLayout;
    const optionsByKey: Map<string, IMasterLayout> = new Map<string, IMasterLayout>();
    this.getCandidateLayouts(part).forEach((candidate: IMasterLayout): void => {
      const settled: IMasterLayout = { ...candidate };
      (["h", "f", "l", "r"] as MasterLayoutPart[]).forEach((other: MasterLayoutPart): void => {
        if (other !== part && current[other] === ELayoutPosition.invisible) {
          settled[other] = ELayoutPosition.invisible;
        }
      });
      const key: string = masterLayoutKey(settled);
      if (!optionsByKey.has(key)) {
        optionsByKey.set(key, settled);
      }
    });
    return Array.from(optionsByKey.values());
  };

  handleRegionOptionSelect = (part: MasterLayoutPart, key: number | string | null): void => {
    const option: IMasterLayout | undefined = parseMasterLayoutKey(String(key));
    if (!option) return;
    this.handleLayoutSelect(option.h, option.f, option.l, option.r, part);
  };

  renderRegionControl = (part: MasterLayoutPart): JSX.Element => {
    const current: IMasterLayout = this.currentLayout;
    const name: string = MASTER_LAYOUT_PART_NAMES[part];
    const menuOptions: KuikaDropdownMenuItemType[] = this.getRegionOptions(part).map(
      (option: IMasterLayout): KuikaDropdownMenuItemType => {
        const key: string = masterLayoutKey(option);
        return {
          key,
          label: describeLayoutRegion(part, option),
          icon: this.getLayoutOption(option.h, option.f, option.l, option.r, part, true),
          dataTestId: `masterLayoutOption-${part}-${key}`
        };
      }
    );

    return (
      <div key={part} className={`kuika-master-layout__slot kuika-master-layout__slot--${part}`}>
        <KuikaDropdownMenu
          options={menuOptions}
          selected={masterLayoutKey(current)}
          setSelected={(key: number | string | null): void => this.handleRegionOptionSelect(part, key)}
          header={name}
          placement={MENU_PLACEMENT[part]}
          overlayClassName="kuika-master-layout__menu"
          menuTestId={`masterLayoutMenu-${part}`}
        >
          {(open: boolean): JSX.Element => (
            <button
              type="button"
              data-testid={`masterLayoutControl-${part}`}
              aria-label={`${name}: ${describeLayoutRegion(part, current)}`}
              aria-haspopup="menu"
              aria-expanded={open}
              className={classNames("kuika-master-layout__control", `kuika-master-layout__control--${part}`, {
                "kuika-master-layout__control--open": open
              })}
            >
              {this.getCurrentLayoutOption(part)}
              <KuikaSvg name="chevron-down" type="arrows" width={12} height={12} />
            </button>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  getLayoutPreview = (selectedDi?: IDisplayItem) => {
    return this.getLayout(
      1,
      12,
      22,
      34,
      this.currentH,
      this.currentF,
      this.currentL,
      this.currentR,
      undefined,
      false,
      true,
      selectedDi
    );
  };

  getCurrentLayoutOption = (activePart: "h" | "f" | "l" | "r") => {
    return this.getLayoutOption(this.currentH, this.currentF, this.currentL, this.currentR, activePart, true);
  };

  getLayoutOption = (
    hPosition: ELayoutPosition,
    fPosition: ELayoutPosition,
    lPosition: ELayoutPosition,
    rPosition: ELayoutPosition,
    activePart?: "h" | "f" | "l" | "r",
    needBorder?: boolean
  ) => {
    return (
      <div style={{ width: "30px" }}>
        {this.getLayout(1, 3, 7, 10, hPosition, fPosition, lPosition, rPosition, activePart, needBorder)}
      </div>
    );
  };

  getLayout = (
    margin: number,
    horizantalPartHeight: number,
    verticalPartWidth: number,
    minCenterHeight: number,
    hPosition: ELayoutPosition,
    fPosition: ELayoutPosition,
    lPosition: ELayoutPosition,
    rPosition: ELayoutPosition,
    activePart?: "h" | "f" | "l" | "r",
    needBorder?: boolean,
    preview?: boolean,
    selectedDi?: IDisplayItem
  ) => {
    // --blue-1 all but vanished on the dark panel; --blue-2 keeps an unselected region visible in both themes.
    const secondaryBgColor = "var(--blue-2)";
    const primaryBgColor = "var(--blue-5)";
    let backgroundColor;

    const getCenterHeight = () => {
      return (
        minCenterHeight + (hPosition <= 0 ? horizantalPartHeight : 0) + (fPosition <= 0 ? horizantalPartHeight : 0)
      );
    };

    const horizantal = (part: "h" | "f") => {
      backgroundColor =
        preview && part === "h" && selectedDi?.id === SpecialDIIds.layoutHeaderDI ? primaryBgColor : secondaryBgColor;
      backgroundColor =
        preview && part === "f" && selectedDi?.id === SpecialDIIds.layoutFooterDI ? primaryBgColor : backgroundColor;
      return (
        <div
          onClick={(e) =>
            preview && part === "h"
              ? this.props.sd.setSelectedDisplayItem(SpecialDIIds.layoutHeaderDI.toString())
              : preview && part === "f" && this.props.sd.setSelectedDisplayItem(SpecialDIIds.layoutFooterDI.toString())
          }
          style={{
            backgroundColor: activePart === part ? primaryBgColor : backgroundColor,
            margin: `${margin}px`,
            height: `${horizantalPartHeight}px`,
            borderRadius: "var(--radius-xs)",
            cursor: "pointer"
          }}
        ></div>
      );
    };

    const vertical = (part: "l" | "r") => {
      backgroundColor =
        preview && part === "l" && selectedDi?.id === SpecialDIIds.layoutLeftDI ? primaryBgColor : secondaryBgColor;
      backgroundColor =
        preview && part === "r" && selectedDi?.id === SpecialDIIds.layoutRightDI ? primaryBgColor : backgroundColor;

      return (
        <div
          onClick={(e) =>
            preview && part === "l"
              ? this.props.sd.setSelectedDisplayItem(SpecialDIIds.layoutLeftDI.toString())
              : preview && part === "r" && this.props.sd.setSelectedDisplayItem(SpecialDIIds.layoutRightDI.toString())
          }
          style={{
            backgroundColor: activePart === part ? primaryBgColor : backgroundColor,
            margin: `${margin}px`,
            flexBasis: `${verticalPartWidth}px`,
            borderRadius: "var(--radius-xs)",
            cursor: "pointer"
          }}
        ></div>
      );
    };

    const h = (position: number) => {
      if (position === hPosition) return horizantal("h");
    };

    const f = (position: number) => {
      if (position === fPosition) return horizantal("f");
    };

    const l = (position: number) => {
      if (position === lPosition) return vertical("l");
    };

    const r = (position: number) => {
      if (position === rPosition) return vertical("r");
    };

    return (
      <div className={needBorder === true ? "kuika-master-layout__thumbnail" : undefined}>
        {h(1)}
        <div style={{ display: "flex" }}>
          {l(1)}
          <div style={{ flex: "auto" }}>
            {h(2)}
            <div style={{ display: "flex" }}>
              {l(2)}
              <div
                // Only the live preview selects the screen; a thumbnail inside a control or menu row must not.
                onClick={
                  preview ? () => this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString()) : undefined
                }
                style={{ fontSize: 8, flex: "auto", height: `${getCenterHeight()}px` }}
              ></div>
              {r(2)}
            </div>
            {f(2)}
          </div>
          {r(1)}
        </div>
        {f(1)}
      </div>
    );
  };

  render() {
    const sd = this.props.sd.getScreenDesign();
    const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
    return (
      <div className="kuika-master-layout">
        <KuikaSmallMediumText className="kuika-master-layout__title">Layout Configuration</KuikaSmallMediumText>
        {/* A grid whose cells are the regions being configured: every control is docked on the
            edge of the window it changes, and nothing is positioned against the panel's width. */}
        <div className="kuika-master-layout__stage">
          {MASTER_LAYOUT_PARTS.map((part: MasterLayoutPart): JSX.Element => this.renderRegionControl(part))}
          <div className="kuika-master-layout__window">
            <div className="kuika-master-layout__window-bar" aria-hidden="true">
              <span className="kuika-master-layout__window-dot" />
              <span className="kuika-master-layout__window-dot" />
              <span className="kuika-master-layout__window-dot" />
            </div>
            <div className="kuika-master-layout__preview">{this.getLayoutPreview(selectedDi)}</div>
          </div>
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.PropertyFixedValueChanged ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const masterLayoutComponent = connect(null)(
  withContext<IMasterLayoutComponentProps>(withSD<IMasterLayoutComponentProps>(MasterLayoutComponent))
);
export { masterLayoutComponent as MasterLayoutComponent };
// The unwrapped class, for tests that pass a stand-in `sd`.
export { MasterLayoutComponent as MasterLayoutComponentBase };
